import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import {
	formatDateForDisplay,
	formatDateForInput,
} from "../../lib/dateHelpers";
import { sanitizeInterviewHtml, htmlToPlainText } from "../../lib/sanitizeWordHtml";
import type { Article } from "../../hooks/useArticles";
import {
	compareArticles,
	isPinned,
	newestId,
	sortArticles,
	sortKeyBetween,
	sortKeyForNew,
} from "../../lib/articleOrder";
import { useCategories } from "../../hooks/useCategories";
import ArticlePreview from "./components/ArticlePreview";
import ImageUploader from "../../components/base/ImageUploader";
import { AD_FORMATS, adFormatOf, type AdFormat } from "../../hooks/useSponsors";
import VideoUploader from "../../components/base/VideoUploader";
import RichTextEditor from "../../components/base/RichTextEditor";
import StatsNewsEditor from "./components/StatsNewsEditor";
import NewsletterTab from "./components/NewsletterTab";
import CategoryManager from "./components/CategoryManager";
import AnaliticasTab from "./components/AnaliticasTab";
import { useVisitStats } from "../../hooks/useVisitStats";
import SEO from "@/components/SEO";
import { buildSearchText, matchesSearchText, searchTerms } from "../../lib/articleSearch";

// Peso máximo del arte publicitario: los banners se cargan en cada página junto a las fotos de las noticias.
const BANNER_MAX_MB = 1;

/** Medidas del arte según el tamaño elegido, para poder indicárselas al anunciante. */
function BannerSpecs({ adType }: { adType: string }) {
	const selected = adFormatOf(adType);
	return (
		<div className="mt-3 rounded-lg border border-blue-100 bg-blue-50/60 px-3 py-2.5">
			<p className="text-[#0B1F3A] text-xs font-semibold">
				<i className="ri-ruler-2-line mr-1" />
				Tamaño del arte: {AD_FORMATS[selected].width} × {AD_FORMATS[selected].height} px ({AD_FORMATS[selected].label})
			</p>
			<p className="text-slate-500 text-[11px] mt-0.5">
				JPG, PNG, WebP o GIF animado · Peso máximo {BANNER_MAX_MB} MB · El tamaño se elige en «Tamaño / tipo de anuncio».
			</p>
			<ul className="mt-2 space-y-0.5">
				{(Object.keys(AD_FORMATS) as AdFormat[]).map((key) => (
					<li
						key={key}
						className={`text-[11px] ${key === selected ? "text-[#0B1F3A] font-semibold" : "text-slate-400"}`}
					>
						{AD_FORMATS[key].label}: {AD_FORMATS[key].width} × {AD_FORMATS[key].height} px · {AD_FORMATS[key].where}
					</li>
				))}
			</ul>
		</div>
	);
}

const ADMIN_PASSWORD_HASH =
	"a665a45920422f9d417e4867efdc4fb8a04a1f3fff1fa07e998e86f7f7a27ae3";

async function sha256(message: string): Promise<string> {
	const encoder = new TextEncoder();
	const data = encoder.encode(message);
	const hashBuffer = await crypto.subtle.digest("SHA-256", data);
	const hashArray = Array.from(new Uint8Array(hashBuffer));
	return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

function generateSlug(title: string): string {
	return title
		.toLowerCase()
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.replace(/[^a-z0-9\s-]/g, "")
		.trim()
		.replace(/\s+/g, "-");
}

interface ArticleFormData {
	title: string;
	excerpt: string;
	content: string;
	primary_category: string;
	secondary_categories: string[];
	subcategory: string;
	author: string;
	date: string;
	read_time: string;
	image: string;
	featured: boolean;
	video_url: string;
	is_international: boolean;
	additional_images: string[];
	image_captions: string[];
}

interface PreviewFormData {
	title: string;
	excerpt: string;
	category: string;
	category_color: string;
	author: string;
	date: string;
	read_time: string;
	image: string;
	featured: boolean;
	content: string;
}

interface Sponsor {
	id: number;
	company_name: string;
	logo_url: string;
	banner_url: string;
	website_url: string;
	contact_name: string;
	contact_email: string;
	contact_phone: string;
	ad_type: string;
	placement: string;
	active: boolean;
	start_date: string;
	end_date: string;
	notes: string;
	created_at: string;
}

interface SponsorFormData {
	company_name: string;
	logo_url: string;
	banner_url: string;
	website_url: string;
	contact_name: string;
	contact_email: string;
	contact_phone: string;
	ad_type: string;
	placement: string;
	active: boolean;
	start_date: string;
	end_date: string;
	notes: string;
}

const turismoSubcategories = [
	"Destinos",
	"Hoteles",
	"Inversión",
	"Asociaciones",
];

const emptyForm: ArticleFormData = {
	title: "",
	excerpt: "",
	content: "",
	primary_category: "",
	secondary_categories: [],
	subcategory: "",
	author: "Carmen Bretón",
	date: new Date().toISOString().split("T")[0],
	read_time: "4 min",
	image: "",
	featured: false,
	video_url: "",
	is_international: false,
	additional_images: [],
	image_captions: [],
};

function getReadTime(content: string): string {
	const text = content.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
	const words = text ? text.split(" ").length : 0;
	const minutes = Math.max(1, Math.ceil(words / 200));
	return `${minutes} min`;
}

const emptySponsorForm: SponsorFormData = {
	company_name: "",
	logo_url: "",
	banner_url: "",
	website_url: "",
	contact_name: "",
	contact_email: "",
	contact_phone: "",
	ad_type: "banner",
	placement: "sidebar",
	active: true,
	start_date: "",
	end_date: "",
	notes: "",
};

type AdminTab =
	| "dashboard"
	| "analiticas"
	| "articles"
	| "sponsors"
	| "newsletter"
	| "messages"
	| "publicidad"
	| "estadisticas"
	| "categorias";

export default function Admin() {
	const [isLoggedIn, setIsLoggedIn] = useState(false);
	const [loginEmail, setLoginEmail] = useState("");
	const [password, setPassword] = useState("");
	const [loginError, setLoginError] = useState("");
	const [activeTab, setActiveTab] = useState<AdminTab>("dashboard");

	// Categories from Supabase
	const {
		categories,
		categoryColors,
		standardCategories,
		loading: loadingCategories,
		refetch: refetchCategories,
	} = useCategories();

	const contentSections = useMemo(
		() =>
			categories.map((cat) => ({ id: cat.name, label: cat.name })),
		[categories],
	);

	// Messages state
	const [messages, setMessages] = useState<
		{
			id: number;
			name: string;
			email: string;
			tipo_consulta: string;
			asunto: string;
			mensaje: string;
			read: boolean;
			created_at: string;
		}[]
	>([]);
	const [loadingMessages, setLoadingMessages] = useState(false);
	const [messageFilter, setMessageFilter] = useState("all");
	const [selectedMessage, setSelectedMessage] = useState<number | null>(null);

	// Change password state
	const [showChangePassword, setShowChangePassword] = useState(false);
	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmNewPassword, setConfirmNewPassword] = useState("");
	const [passwordMessage, setPasswordMessage] = useState("");

	// Articles state
	const [articles, setArticles] = useState<Article[]>([]);
	const [loadingArticles, setLoadingArticles] = useState(true);
	const [editingArticle, setEditingArticle] = useState<Article | null>(null);
	const [formData, setFormData] = useState<ArticleFormData>(emptyForm);
	const [saving, setSaving] = useState(false);
	const [message, setMessage] = useState("");
	const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
	const [searchTerm, setSearchTerm] = useState("");

	// Newsletter state
	const [subscribers, setSubscribers] = useState<
		{
			id: number;
			email: string;
			name: string;
			subscribed_at: string;
			active: boolean;
		}[]
	>([]);
	const [loadingSubscribers, setLoadingSubscribers] = useState(false);

	// Sponsors state
	const [sponsors, setSponsors] = useState<Sponsor[]>([]);
	const [loadingSponsors, setLoadingSponsors] = useState(false);
	const [editingSponsor, setEditingSponsor] = useState<Sponsor | null>(null);
	const [sponsorForm, setSponsorForm] =
		useState<SponsorFormData>(emptySponsorForm);
	const [sponsorMessage, setSponsorMessage] = useState("");
	const [deleteSponsorConfirm, setDeleteSponsorConfirm] = useState<
		number | null
	>(null);
	const [showSponsorForm, setShowSponsorForm] = useState(false);

	// Publicidad requests state
	const [adRequests, setAdRequests] = useState<
		{
			id: number;
			empresa: string;
			name: string;
			email: string;
			telefono: string;
			tipo_espacio: string;
			mensaje: string;
			read: boolean;
			created_at: string;
		}[]
	>([]);
	const [loadingAdRequests, setLoadingAdRequests] = useState(false);
	const [adRequestFilter, setAdRequestFilter] = useState("all");
	const [selectedAdRequest, setSelectedAdRequest] = useState<number | null>(
		null,
	);

	// Portal cleanup state
	const [cleanupLoading, setCleanupLoading] = useState(false);
	const [cleanupMessage, setCleanupMessage] = useState("");
	const [showCleanupConfirm, setShowCleanupConfirm] = useState(false);



	const [realCounts, setRealCounts] = useState<Record<string, number>>({});

	// Estadísticas de visitas para el resumen del dashboard
	const { totals: visitTotals, needsSetup: visitsNeedSetup } = useVisitStats(30);

	useEffect(() => {
		const logged = localStorage.getItem("aviacion_admin_email");
		if (logged) {
			setIsLoggedIn(true);
			setLoginEmail(logged);
		}
	}, []);

	const fetchArticles = useCallback(async () => {
		setLoadingArticles(true);
		const { data } = await supabase
			.from("articles")
			.select("*")
			.order("created_at", { ascending: false });
		setArticles(data || []);
		setLoadingArticles(false);

		// Calcular conteos reales por categoría
		const counts: Record<string, number> = {};
		(data || []).forEach((a) => {
			counts[a.category] = (counts[a.category] || 0) + 1;
		});
		setRealCounts(counts);
	}, []);

	const fetchMessages = useCallback(async () => {
		setLoadingMessages(true);
		const { data } = await supabase
			.from("contact_messages")
			.select("*")
			.order("created_at", { ascending: false });
		setMessages(data || []);
		setLoadingMessages(false);
	}, []);

	const fetchSubscribers = useCallback(async () => {
		setLoadingSubscribers(true);
		const { data } = await supabase
			.from("newsletter_subscribers")
			.select("*")
			.order("subscribed_at", { ascending: false });
		setSubscribers(data || []);
		setLoadingSubscribers(false);
	}, []);

	const fetchSponsors = useCallback(async () => {
		setLoadingSponsors(true);
		const { data } = await supabase
			.from("sponsors")
			.select("*")
			.order("created_at", { ascending: false });
		setSponsors(data || []);
		setLoadingSponsors(false);
	}, []);

	const fetchAdRequests = useCallback(async () => {
		setLoadingAdRequests(true);
		const { data } = await supabase
			.from("advertising_requests")
			.select("*")
			.order("created_at", { ascending: false });
		setAdRequests(data || []);
		setLoadingAdRequests(false);
	}, []);

	useEffect(() => {
		if (isLoggedIn) {
			fetchArticles();
			fetchSubscribers();
			fetchSponsors();
			fetchMessages();
			fetchAdRequests();
		}
	}, [
		isLoggedIn,
		fetchArticles,
		fetchSubscribers,
		fetchSponsors,
		fetchMessages,
		fetchAdRequests,
	]);

	const handleLogin = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!loginEmail.trim() || !password.trim()) {
			setLoginError("Por favor ingresa email y contraseña.");
			return;
		}
		const hash = await sha256(password);
		const { data } = await supabase
			.from("admin_settings")
			.select("email, password_hash")
			.eq("email", loginEmail.trim())
			.eq("password_hash", hash)
			.maybeSingle();

		if (data) {
			localStorage.setItem("aviacion_admin_email", loginEmail.trim());
			setIsLoggedIn(true);
			setLoginError("");
			setPassword("");
		} else {
			setLoginError("Email o contraseña incorrectos.");
		}
	};

	const handleLogout = () => {
		localStorage.removeItem("aviacion_admin_email");
		setIsLoggedIn(false);
		setPassword("");
		setLoginEmail("");
	};

	const handleChangePassword = async (e: React.FormEvent) => {
		e.preventDefault();
		setPasswordMessage("");
		if (!currentPassword || !newPassword || !confirmNewPassword) {
			setPasswordMessage("Error: Todos los campos son obligatorios.");
			return;
		}
		if (newPassword !== confirmNewPassword) {
			setPasswordMessage("Error: Las nuevas contraseñas no coinciden.");
			return;
		}
		if (newPassword.length < 6) {
			setPasswordMessage(
				"Error: La contraseña debe tener al menos 6 caracteres.",
			);
			return;
		}
		const currentHash = await sha256(currentPassword);
		const newHash = await sha256(newPassword);

		const { data } = await supabase
			.from("admin_settings")
			.select("id")
			.eq("email", loginEmail)
			.eq("password_hash", currentHash)
			.maybeSingle();

		if (!data) {
			setPasswordMessage("Error: La contraseña actual es incorrecta.");
			return;
		}

		const { error } = await supabase
			.from("admin_settings")
			.update({ password_hash: newHash })
			.eq("email", loginEmail);

		if (error) {
			setPasswordMessage("Error al cambiar la contraseña. Intenta de nuevo.");
			return;
		}

		setPasswordMessage("Contraseña actualizada correctamente.");
		setCurrentPassword("");
		setNewPassword("");
		setConfirmNewPassword("");
		setTimeout(() => setShowChangePassword(false), 1500);
	};

	// Article handlers
	const allSelectedCategories = formData.primary_category
		? [formData.primary_category, ...formData.secondary_categories]
		: formData.secondary_categories;

	const handleEdit = (article: Article) => {
		setEditingArticle(article);
		const articleTags: string[] = Array.isArray(article.tags) && article.tags.length > 0
			? article.tags
			: [article.category];
		const rawPrimary = articleTags[0] || article.category;
		// Si la categoría no existe en categoryColors (ej: "Internacional" como categoría huérfana),
		// forzar a que use article.category directamente y limpiar tags para evitar select vacío
		const primary = categoryColors[rawPrimary] ? rawPrimary : (categoryColors[article.category] ? article.category : "");
		const secondaries = articleTags.length > 1
			? articleTags.slice(1).filter((t) => t !== primary && categoryColors[t])
			: [];
		// Si la categoría primaria está vacía pero hay secundarias, promover la primera
		const finalPrimary = primary || secondaries[0] || "";
		const finalSecondaries = finalPrimary === secondaries[0] ? secondaries.slice(1) : secondaries;
		setFormData({
			title: article.title,
			excerpt: htmlToPlainText(article.excerpt),
			// Cargar como HTML enriquecido; el fast-path envuelve texto plano heredado en <p>
			content: sanitizeInterviewHtml(article.content),
			primary_category: finalPrimary,
			secondary_categories: finalSecondaries,
			subcategory: article.subcategory || "",
			author: article.author,
			date: formatDateForInput(article.date),
			read_time: article.read_time,
			image: article.image,
			featured: article.featured,
			video_url: article.video_url || "",
			is_international: article.is_international || false,
			additional_images: Array.isArray(article.additional_images)
				? article.additional_images
				: [],
			image_captions: Array.isArray(article.image_captions)
				? article.image_captions
				: [],
		});
		setMessage("");
		setActiveTab("articles");
		window.scrollTo({ top: 0, behavior: "smooth" });
	};

	const handleNew = () => {
		setEditingArticle(null);
		setFormData({ ...emptyForm, primary_category: "", secondary_categories: [] });
		setMessage("");
	};

	const handleSave = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!formData.title.trim()) {
			setMessage("Error: El título es obligatorio.");
			return;
		}
		if (!formData.excerpt.trim()) {
			setMessage("Error: El extracto es obligatorio.");
			return;
		}
		if (!formData.content.trim()) {
			setMessage("Error: El contenido es obligatorio.");
			return;
		}
		if (!formData.date) {
			setMessage("Error: La fecha es obligatoria.");
			return;
		}
		if (!formData.primary_category && formData.secondary_categories.length === 0) {
			setMessage("Error: Debes seleccionar al menos una categoría.");
			return;
		}

		// Si no hay imagen, usar un fallback por defecto
		const fallbackImage =
			"https://readdy.ai/api/search-image?query=dramatic%20wide%20shot%20of%20a%20modern%20commercial%20airplane%20taking%20off%20from%20a%20Caribbean%20airport%20with%20blue%20sky%20and%20tropical%20clouds%20aviation%20photography%20high%20quality%20editorial&width=1200&height=675&seq=article-fallback&orientation=landscape";
		const finalImage = formData.image.trim() || fallbackImage;

		setSaving(true);
		setMessage("");
		const displayDate = formatDateForDisplay(formData.date);
		const allTags = formData.primary_category
			? [formData.primary_category, ...formData.secondary_categories]
			: formData.secondary_categories;
		// Si la noticia es internacional, agregar "Internacional" a los tags automáticamente
		if (formData.is_international && !allTags.includes("Internacional")) {
			allTags.push("Internacional");
		}
		const dataToSave = {
			title: formData.title,
			excerpt: htmlToPlainText(formData.excerpt),
			content: sanitizeInterviewHtml(formData.content),
			category: formData.primary_category || formData.secondary_categories[0] || "",
			category_color: categoryColors[formData.primary_category] || categoryColors[formData.secondary_categories[0]] || "",
			subcategory: formData.subcategory || null,
			author: "Carmen Bretón",
			date: displayDate,
			read_time: getReadTime(formData.content),
			image: finalImage,
			featured: formData.featured,
			slug: generateSlug(formData.title),
			tags: allTags.length > 0 ? allTags : [],
			video_url: formData.video_url || null,
			is_international: formData.is_international,
			additional_images: formData.additional_images.length > 0 ? formData.additional_images : [],
			image_captions: formData.image_captions.length > 0 ? formData.image_captions : [],
		};
		try {
			if (editingArticle) {
				// Si cambia la fecha, la noticia vuelve a su lugar por fecha
				const resetPlace =
					editingArticle.sort_at && editingArticle.date !== displayDate
						? { sort_at: null }
						: {};
				const { error } = await supabase
					.from("articles")
					.update({ ...dataToSave, ...resetPlace })
					.eq("id", editingArticle.id);
				if (error) throw error;
				setMessage("Artículo actualizado correctamente.");
			} else {
				// La última noticia subida sale primero; `sort_at` la deja también arriba
				// del orden por fecha cuando deje de ser la más reciente
				const sortAt = new Date(sortKeyForNew(articles)).toISOString();
				const { error } = await supabase
					.from("articles")
					.insert([{ ...dataToSave, sort_at: sortAt }]);
				if (error) throw error;
				setMessage("Artículo creado correctamente.");
				setFormData({ ...emptyForm });
				setEditingArticle(null);
			}
			fetchArticles();
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : "Error desconocido";
			console.error("Save article error:", errorMessage);
			setMessage("Error: " + errorMessage);
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async (id: number) => {
		setSaving(true);
		try {
			const { error } = await supabase.from("articles").delete().eq("id", id);
			if (error) throw error;
			setMessage("Artículo eliminado correctamente.");
			setDeleteConfirm(null);
			fetchArticles();
		} catch (err) {
			setMessage(
				"Error al eliminar: " +
					(err instanceof Error ? err.message : "Error desconocido"),
			);
		} finally {
			setSaving(false);
		}
	};

	// ─── Orden manual: noticias fijadas ─────────────────────────────────────────
	// Guarda `position` 1..N según `pinnedIds`; `unpinId` vuelve al orden por fecha.
	const savePinnedOrder = async (pinnedIds: number[], unpinId?: number) => {
		const target = new Map<number, number | null>(pinnedIds.map((id, i) => [id, i + 1]));
		if (unpinId !== undefined) target.set(unpinId, null);
		const changes = articles.filter(
			(a) => target.has(a.id) && (a.position ?? null) !== target.get(a.id),
		);
		if (changes.length === 0) return;

		setSaving(true);
		try {
			const results = await Promise.all(
				changes.map((a) =>
					supabase.from("articles").update({ position: target.get(a.id) }).eq("id", a.id),
				),
			);
			const failed = results.find((r) => r.error)?.error;
			if (failed) {
				throw new Error(
					failed.code === "42703" || failed.code === "PGRST204"
						? "Falta la columna \"position\" en la tabla articles de Supabase."
						: failed.message,
				);
			}
			setMessage(unpinId !== undefined ? "Noticia desfijada." : "Orden actualizado.");
			fetchArticles();
		} catch (err) {
			setMessage(
				"Error al cambiar el orden: " +
					(err instanceof Error ? err.message : "Error desconocido"),
			);
		} finally {
			setSaving(false);
		}
	};

	const pinnedIds = articles.filter(isPinned).sort(compareArticles).map((a) => a.id);
	// La última noticia subida sale siempre primero, incluso sobre las fijadas
	const latestId = newestId(articles);

	const handlePin = (id: number) => savePinnedOrder([...pinnedIds, id]);

	const handleUnpin = (id: number) =>
		savePinnedOrder(pinnedIds.filter((p) => p !== id), id);

	const handleMovePinned = (id: number, direction: -1 | 1) => {
		const from = pinnedIds.indexOf(id);
		const to = from + direction;
		if (from < 0 || to < 0 || to >= pinnedIds.length) return;
		const next = [...pinnedIds];
		[next[from], next[to]] = [next[to], next[from]];
		savePinnedOrder(next);
	};

	// Mover sin fijar: la noticia queda entre sus nuevas vecinas (según la lista
	// visible) guardando `sort_at`, y sigue bajando cuando se publican noticias nuevas.
	const handleMoveFree = async (id: number, direction: -1 | 1) => {
		const list = sortedArticles.filter((a) => !isPinned(a) && a.id !== latestId);
		const i = list.findIndex((a) => a.id === id);
		const j = i + direction;
		if (i < 0 || j < 0 || j >= list.length) return;
		const [above, below] =
			direction === -1 ? [list[j - 1], list[j]] : [list[j], list[j + 1]];
		const sortAt = new Date(sortKeyBetween(above, below)).toISOString();

		setSaving(true);
		try {
			const { error } = await supabase
				.from("articles")
				.update({ sort_at: sortAt })
				.eq("id", id);
			if (error) {
				throw new Error(
					error.code === "42703" || error.code === "PGRST204"
						? "Falta la columna \"sort_at\" en la tabla articles de Supabase."
						: error.message,
				);
			}
			setMessage("Noticia movida.");
			fetchArticles();
		} catch (err) {
			setMessage(
				"Error al mover: " + (err instanceof Error ? err.message : "Error desconocido"),
			);
		} finally {
			setSaving(false);
		}
	};

	// Sponsor handlers
	const handleEditSponsor = (sponsor: Sponsor) => {
		setEditingSponsor(sponsor);
		setSponsorForm({
			company_name: sponsor.company_name,
			logo_url: sponsor.logo_url || "",
			banner_url: sponsor.banner_url || "",
			website_url: sponsor.website_url || "",
			contact_name: sponsor.contact_name || "",
			contact_email: sponsor.contact_email || "",
			contact_phone: sponsor.contact_phone || "",
			ad_type: sponsor.ad_type || "banner",
			placement: sponsor.placement || "sidebar",
			active: sponsor.active,
			start_date: sponsor.start_date || "",
			end_date: sponsor.end_date || "",
			notes: sponsor.notes || "",
		});
		setSponsorMessage("");
		setShowSponsorForm(true);
		window.scrollTo({ top: 0, behavior: "smooth" });
	};

	const handleNewSponsor = () => {
		setEditingSponsor(null);
		setSponsorForm({ ...emptySponsorForm });
		setSponsorMessage("");
		setShowSponsorForm(true);
	};

	const handleSaveSponsor = async (e: React.FormEvent) => {
		e.preventDefault();
		setSaving(true);
		setSponsorMessage("");
		try {
			if (editingSponsor) {
				const { error } = await supabase
					.from("sponsors")
					.update(sponsorForm)
					.eq("id", editingSponsor.id);
				if (error) throw error;
				setSponsorMessage("Sponsor actualizado correctamente.");
			} else {
				const { error } = await supabase.from("sponsors").insert([sponsorForm]);
				if (error) throw error;
				setSponsorMessage("Sponsor agregado correctamente.");
				setSponsorForm({ ...emptySponsorForm });
				setEditingSponsor(null);
				setShowSponsorForm(false);
			}
			fetchSponsors();
		} catch (err) {
			setSponsorMessage(
				"Error: " + (err instanceof Error ? err.message : "Error desconocido"),
			);
		} finally {
			setSaving(false);
		}
	};

	const handleDeleteSponsor = async (id: number) => {
		setSaving(true);
		try {
			const { error } = await supabase.from("sponsors").delete().eq("id", id);
			if (error) throw error;
			setSponsorMessage("Sponsor eliminado.");
			setDeleteSponsorConfirm(null);
			fetchSponsors();
		} catch (err) {
			setSponsorMessage(
				"Error: " + (err instanceof Error ? err.message : "Error desconocido"),
			);
		} finally {
			setSaving(false);
		}
	};

	const handleCleanupPortal = async () => {
		setCleanupLoading(true);
		setCleanupMessage("");
		try {
			const { data, error } = await supabase.functions.invoke(
				"cleanup-portal",
				{
					body: {},
				},
			);
			if (error) throw error;
			setCleanupMessage(
				`Portal vaciado: ${data?.deleted?.articles || 0} noticias, ${data?.deleted?.sector_stats || 0} stats eliminados.`,
			);
			fetchArticles();
		} catch (err) {
			setCleanupMessage(
				"Error: " +
					(err instanceof Error ? err.message : "No se pudo vaciar el portal"),
			);
		} finally {
			setCleanupLoading(false);
			setShowCleanupConfirm(false);
		}
	};

	const [categoryFilter, setCategoryFilter] = useState<string>("all");
	const [subcategoryFilter, setSubcategoryFilter] = useState<string>("all");

	// Unique categories from loaded articles (including orphaned ones like "Actualidad")
	const uniqueCategories = useMemo(() => {
		const cats = new Set<string>();
		articles.forEach((a) => {
			if (a.category) cats.add(a.category);
		});
		return Array.from(cats).sort();
	}, [articles]);

	const adminSearchTerms = searchTerms(searchTerm);
	const filteredArticles = articles.filter((a) => {
		const matchesSearch = matchesSearchText(buildSearchText(a), adminSearchTerms);
		const matchesCategory =
			categoryFilter === "all" ||
			a.category === categoryFilter ||
			(Array.isArray(a.tags) && a.tags.includes(categoryFilter));
		const matchesSubcategory =
			subcategoryFilter === "all" || a.subcategory === subcategoryFilter;
		return matchesSearch && matchesCategory && matchesSubcategory;
	});
	const sortedArticles = sortArticles(filteredArticles, latestId);

	const activeSubscribers = subscribers.filter((s) => s.active).length;

	// ─── LOGIN SCREEN ───────────────────────────────────────────────────────────
	if (!isLoggedIn) {
		return (
			<main
				className="min-h-screen bg-[#0B1F3A] flex items-center justify-center"
				style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
			>
				<SEO />
				<div className="w-full max-w-md mx-6">
					<div className="text-center mb-8">
						<div className="w-16 h-16 flex items-center justify-center bg-white/10 rounded-2xl mx-auto mb-4">
							<i className="ri-shield-keyhole-line text-white text-3xl" />
						</div>
						<h1
							className="text-white font-bold text-2xl mb-2"
							style={{ fontFamily: "'Sora', sans-serif" }}
						>
							Panel de Administración
						</h1>
						<p className="text-blue-300/60 text-sm">
							Aviación Insight RD · Acceso restringido
						</p>
					</div>

					<form
						onSubmit={handleLogin}
						className="bg-white rounded-2xl p-8 space-y-5"
					>
						<div>
							<label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
								Correo electrónico
							</label>
							<input
								type="email"
								value={loginEmail}
								onChange={(e) => setLoginEmail(e.target.value)}
								placeholder="Ingresa tu correo electrónico"
								className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/30 focus:border-[#0B1F3A] transition-all"
								autoFocus
							/>
						</div>
						<div>
							<label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
								Contraseña
							</label>
							<input
								type="password"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								placeholder="Ingresa la contraseña"
								className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/30 focus:border-[#0B1F3A] transition-all"
							/>
						</div>

						{loginError && (
							<div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
								<i className="ri-error-warning-line text-red-500" />
								<p className="text-red-600 text-xs">{loginError}</p>
							</div>
						)}

						<button
							type="submit"
							className="w-full py-3.5 bg-[#0B1F3A] hover:bg-slate-800 text-white font-bold text-sm rounded-xl transition-colors cursor-pointer whitespace-nowrap"
							style={{ fontFamily: "'Sora', sans-serif" }}
						>
							<i className="ri-login-box-line mr-2" />
							Acceder al Panel
						</button>

						<div className="text-center">
							<Link
								to="/"
								className="text-slate-400 text-xs hover:text-slate-600 cursor-pointer"
							>
								← Volver al inicio
							</Link>
						</div>
					</form>
				</div>
			</main>
		);
	}

	// ─── ADMIN DASHBOARD ────────────────────────────────────────────────────────
	const tabs: { id: AdminTab; label: string; icon: string }[] = [
		{ id: "dashboard", label: "Dashboard", icon: "ri-dashboard-line" },
		{ id: "analiticas", label: "Analíticas", icon: "ri-line-chart-line" },
		{ id: "articles", label: "Noticias", icon: "ri-article-line" },
		{
			id: "sponsors",
			label: "Sponsors & Publicidad",
			icon: "ri-megaphone-line",
		},
		{ id: "categorias", label: "Categorías", icon: "ri-apps-line" },
		{ id: "newsletter", label: "Newsletter", icon: "ri-mail-line" },
		{ id: "estadisticas", label: "Estadísticas", icon: "ri-file-chart-line" },
		{ id: "messages", label: "Mensajes", icon: "ri-message-3-line" },
		{
			id: "publicidad",
			label: "Solicitudes Publicidad",
			icon: "ri-advertisement-line",
		},
	];

	return (
		<main
			className="min-h-screen bg-[#F4F7FB]"
			style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
		>
			<SEO />
			{/* Header */}
			<header className="bg-[#0B1F3A] sticky top-0 z-40">
				<div className="max-w-screen-xl mx-auto px-6 py-4 flex items-center justify-between">
					<div className="flex items-center gap-3">
						<div className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-lg">
							<i className="ri-dashboard-line text-white text-lg" />
						</div>
						<div>
							<h1
								className="text-white font-bold text-sm"
								style={{ fontFamily: "'Sora', sans-serif" }}
							>
								Panel Admin
							</h1>
							<p className="text-blue-300/50 text-[10px]">
								Aviación Insight RD · Carmen Bretón
							</p>
						</div>
					</div>
					<div className="flex items-center gap-3">
						<Link
							to="/"
							className="hidden sm:flex items-center gap-1.5 px-3 py-2 text-blue-300/70 hover:text-white text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
						>
							<i className="ri-home-line" /> Inicio
						</Link>
						<button
							onClick={() => {
								setShowChangePassword(true);
								setPasswordMessage("");
							}}
							className="hidden sm:flex items-center gap-1.5 px-3 py-2 text-blue-300/70 hover:text-white text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
							title="Cambiar contraseña"
						>
							<i className="ri-lock-password-line" /> Seguridad
						</button>
						<button
							onClick={handleLogout}
							className="flex items-center gap-1.5 px-3 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
						>
							<i className="ri-logout-box-line" /> Cerrar sesión
						</button>
					</div>
				</div>

				{/* Tabs */}
				<div className="max-w-screen-xl mx-auto px-6 overflow-x-auto pb-0">
					<div className="flex gap-1 min-w-max">
						{tabs.map((tab) => (
							<button
								key={tab.id}
								onClick={() => {
									setActiveTab(tab.id);
									setShowChangePassword(false);
								}}
								className={`flex items-center gap-1.5 px-4 py-3 text-xs font-semibold transition-all cursor-pointer whitespace-nowrap border-b-2 ${
									activeTab === tab.id
										? "text-white border-red-500"
										: "text-blue-300/60 border-transparent hover:text-white hover:border-white/20"
								}`}
								style={{ fontFamily: "'Sora', sans-serif" }}
							>
								<i className={tab.icon} />
								<span className="hidden sm:inline">{tab.label}</span>
							</button>
						))}
					</div>
				</div>
			</header>

			<div className="max-w-screen-xl mx-auto px-6 py-8">
				{/* Change password panel */}
				{showChangePassword && (
					<div className="bg-white rounded-2xl border border-slate-100 overflow-hidden mb-6">
						<div className="bg-[#0B1F3A] px-6 py-4 flex items-center justify-between">
							<h2
								className="text-white font-bold text-sm"
								style={{ fontFamily: "'Sora', sans-serif" }}
							>
								<i className="ri-lock-password-line mr-1.5" />
								Cambiar Contraseña
							</h2>
							<button
								onClick={() => setShowChangePassword(false)}
								className="w-7 h-7 flex items-center justify-center bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors cursor-pointer"
							>
								<i className="ri-close-line text-sm" />
							</button>
						</div>
						<form
							onSubmit={handleChangePassword}
							className="px-6 py-6 space-y-5"
						>
							<div>
								<label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
									Contraseña actual
								</label>
								<input
									type="password"
									value={currentPassword}
									onChange={(e) => setCurrentPassword(e.target.value)}
									placeholder="Contraseña actual"
									className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/30 focus:border-[#0B1F3A] transition-all"
								/>
							</div>
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
								<div>
									<label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
										Nueva contraseña
									</label>
									<input
										type="password"
										value={newPassword}
										onChange={(e) => setNewPassword(e.target.value)}
										placeholder="Mínimo 6 caracteres"
										className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/30 focus:border-[#0B1F3A] transition-all"
									/>
								</div>
								<div>
									<label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
										Confirmar nueva contraseña
									</label>
									<input
										type="password"
										value={confirmNewPassword}
										onChange={(e) => setConfirmNewPassword(e.target.value)}
										placeholder="Repite la nueva contraseña"
										className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/30 focus:border-[#0B1F3A] transition-all"
									/>
								</div>
							</div>
							{passwordMessage && (
								<div
									className={`flex items-center gap-2 rounded-xl px-4 py-3 ${
										passwordMessage.includes("Error")
											? "bg-red-50 border border-red-200 text-red-600"
											: "bg-emerald-50 border border-emerald-200 text-emerald-600"
									}`}
								>
									<i
										className={
											passwordMessage.includes("Error")
												? "ri-error-warning-line"
												: "ri-check-line"
										}
									/>
									<p className="text-sm font-semibold">{passwordMessage}</p>
								</div>
							)}
							<div className="flex gap-3">
								<button
									type="submit"
									disabled={saving}
									className="px-6 py-3 bg-[#0B1F3A] hover:bg-slate-800 text-white font-bold text-sm rounded-xl transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center gap-2"
									style={{ fontFamily: "'Sora', sans-serif" }}
								>
									<i className="ri-save-line" /> Guardar contraseña
								</button>
								<button
									type="button"
									onClick={() => setShowChangePassword(false)}
									className="px-6 py-3 border border-slate-200 text-slate-600 font-semibold text-sm rounded-xl hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
								>
									Cancelar
								</button>
							</div>
						</form>
					</div>
				)}

				{message && (
					<div
						className={`flex items-center gap-2 rounded-xl px-4 py-3 mb-6 ${
							message.includes("Error")
								? "bg-red-50 border border-red-200 text-red-600"
								: "bg-emerald-50 border border-emerald-200 text-emerald-600"
						}`}
					>
						<i
							className={
								message.includes("Error")
									? "ri-error-warning-line"
									: "ri-check-line"
							}
						/>
						<p className="text-sm font-semibold">{message}</p>
					</div>
				)}

				{cleanupMessage && (
					<div
						className={`flex items-center gap-2 rounded-xl px-4 py-3 mb-6 ${
							cleanupMessage.includes("Error")
								? "bg-red-50 border border-red-200 text-red-600"
								: "bg-emerald-50 border border-emerald-200 text-emerald-600"
						}`}
					>
						<i
							className={
								cleanupMessage.includes("Error")
									? "ri-error-warning-line"
									: "ri-check-line"
							}
						/>
						<p className="text-sm font-semibold">{cleanupMessage}</p>
					</div>
				)}

				{/* ── DASHBOARD TAB ── */}
				<div className={activeTab === "dashboard" ? "block" : "hidden"}>
					<div className="space-y-6">
						<div>
							<h2
								className="text-[#0B1F3A] font-extrabold text-2xl mb-1"
								style={{ fontFamily: "'Sora', sans-serif" }}
							>
								Bienvenida, Carmen
							</h2>
							<p className="text-slate-500 text-sm">
								Aquí tienes un resumen de tu portal.
							</p>
						</div>

						{visitsNeedSetup && (
							<button
								onClick={() => setActiveTab("analiticas")}
								className="w-full flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 text-left hover:bg-amber-100/60 transition-colors cursor-pointer"
							>
								<i className="ri-error-warning-line text-amber-600 text-lg mt-0.5" />
								<div>
									<p className="text-amber-800 font-semibold text-sm mb-0.5">
										Activa las estadísticas de visitas
									</p>
									<p className="text-amber-700 text-xs leading-relaxed">
										Falta ejecutar un script en Supabase para empezar a contar
										visitas y lecturas. Haz clic aquí para ver las instrucciones.
									</p>
								</div>
							</button>
						)}

						{/* Dashboard stats - showing only real data */}
						<div className="bg-white rounded-2xl border border-slate-100 p-6">
							<h3
								className="text-[#0B1F3A] font-bold text-sm mb-4"
								style={{ fontFamily: "'Sora', sans-serif" }}
							>
								Resumen del portal
							</h3>
							<div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
								{[
									{
										icon: "ri-eye-line",
										value: visitTotals.today,
										label: "Visitas hoy",
										color: "bg-indigo-50",
										iconColor: "text-indigo-600",
										action: () => setActiveTab("analiticas"),
									},
									{
										icon: "ri-line-chart-line",
										value: visitTotals.last_30_days,
										label: "Visitas (30 días)",
										color: "bg-sky-50",
										iconColor: "text-sky-600",
										action: () => setActiveTab("analiticas"),
									},
									{
										icon: "ri-bar-chart-box-line",
										value: visitTotals.total,
										label: "Visitas totales",
										color: "bg-violet-50",
										iconColor: "text-violet-600",
										action: () => setActiveTab("analiticas"),
									},
									{
										icon: "ri-article-line",
										value: articles.length,
										label: "Artículos publicados",
										color: "bg-blue-50",
										iconColor: "text-blue-600",
										action: () => setActiveTab("articles"),
									},
									{
										icon: "ri-star-line",
										value: articles.filter((a) => a.featured).length,
										label: "Artículos destacados",
										color: "bg-amber-50",
										iconColor: "text-amber-600",
										action: () => setActiveTab("articles"),
									},
									{
										icon: "ri-mail-send-line",
										value: activeSubscribers,
										label: "Suscriptores activos",
										color: "bg-emerald-50",
										iconColor: "text-emerald-600",
										action: () => {
											setActiveTab("newsletter");
											setShowChangePassword(false);
										},
									},
									{
										icon: "ri-megaphone-line",
										value: sponsors.filter((s) => s.active).length,
										label: "Sponsors activos",
										color: "bg-rose-50",
										iconColor: "text-rose-600",
										action: () => {
											setActiveTab("sponsors");
											setShowChangePassword(false);
										},
									},
									{
										icon: "ri-advertisement-line",
										value: adRequests.length,
										label: "Solicitudes publicidad",
										color: "bg-orange-50",
										iconColor: "text-orange-600",
										action: () => {
											setActiveTab("publicidad");
											setShowChangePassword(false);
										},
									},
								].map((stat) => (
									<button
										key={stat.label}
										onClick={stat.action}
										className="bg-white rounded-xl border border-slate-100 p-5 text-center hover:border-slate-200 transition-all cursor-pointer group"
									>
										<div
											className={`w-10 h-10 flex items-center justify-center rounded-xl mx-auto mb-3 ${stat.color} group-hover:scale-110 transition-transform`}
										>
											<i className={`${stat.icon} ${stat.iconColor} text-xl`} />
										</div>
										<p
											className="text-[#0B1F3A] font-extrabold text-3xl mb-1"
											style={{ fontFamily: "'Sora', sans-serif" }}
										>
											{stat.value}
										</p>
										<p className="text-slate-400 text-xs leading-tight">
											{stat.label}
										</p>
									</button>
								))}
							</div>
						</div>

						{/* AI Content Status */}
						<div className="bg-white rounded-2xl border border-slate-100 p-6">
							<div className="flex items-center justify-between mb-4">
								<h3
									className="text-[#0B1F3A] font-bold text-sm"
									style={{ fontFamily: "'Sora', sans-serif" }}
								>
									<i className="ri-database-2-line mr-1.5 text-slate-400" />
									Estado del contenido
								</h3>
								<span className="text-xs text-slate-400">
									Artículos reales publicados
								</span>
							</div>
							<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
								{contentSections.map((sec) => {
									const count = realCounts[sec.id] || 0;
									return (
										<div
											key={sec.id}
											className={`rounded-xl border p-4 transition-colors ${
												count > 0
													? "border-emerald-100 bg-emerald-50/50"
													: "border-slate-100 bg-white"
											}`}
										>
											<div className="flex items-center justify-between mb-2">
												<span
													className="text-[#0B1F3A] text-sm font-bold"
													style={{ fontFamily: "'Sora', sans-serif" }}
												>
													{sec.label}
												</span>
												<span
													className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full ${
														count > 0
															? "bg-emerald-100 text-emerald-700"
															: "bg-slate-100 text-slate-500"
													}`}
												>
													{count > 0 ? "Activo" : "Vacío"}
												</span>
											</div>
											<div className="flex items-baseline gap-2">
												<span
													className="text-2xl font-extrabold text-[#0B1F3A]"
													style={{ fontFamily: "'Sora', sans-serif" }}
												>
													{count}
												</span>
												<span className="text-xs text-slate-400">
													artículos publicados
												</span>
											</div>
										</div>
									);
								})}
							</div>
						</div>

						{/* Quick actions */}
						<div className="bg-white rounded-2xl border border-slate-100 p-6">
							<h3
								className="text-[#0B1F3A] font-bold text-sm mb-4"
								style={{ fontFamily: "'Sora', sans-serif" }}
							>
								Acciones rápidas
							</h3>
							<div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
								<button
									onClick={() => {
										setActiveTab("articles");
										handleNew();
										setShowChangePassword(false);
									}}
									className="flex items-center gap-3 px-4 py-4 bg-[#0B1F3A] hover:bg-slate-800 text-white rounded-xl transition-colors cursor-pointer group"
								>
									<div className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-lg group-hover:bg-white/20 transition-colors">
										<i className="ri-add-line text-lg" />
									</div>
									<div className="text-left">
										<p
											className="font-bold text-sm"
											style={{ fontFamily: "'Sora', sans-serif" }}
										>
											Nueva noticia
										</p>
										<p className="text-white/50 text-xs">Publicar artículo</p>
									</div>
								</button>

								<button
									onClick={() => {
										setActiveTab("sponsors");
										handleNewSponsor();
										setShowChangePassword(false);
									}}
									className="flex items-center gap-3 px-4 py-4 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors cursor-pointer group"
								>
									<div className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-lg group-hover:bg-white/20 transition-colors">
										<i className="ri-megaphone-line text-lg" />
									</div>
									<div className="text-left">
										<p
											className="font-bold text-sm"
											style={{ fontFamily: "'Sora', sans-serif" }}
										>
											Nuevo sponsor
										</p>
										<p className="text-white/50 text-xs">Agregar publicidad</p>
									</div>
								</button>

								<button
									onClick={() => {
										setActiveTab("newsletter");
										setShowChangePassword(false);
									}}
									className="flex items-center gap-3 px-4 py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl transition-colors cursor-pointer group"
								>
									<div className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-lg group-hover:bg-white/20 transition-colors">
										<i className="ri-mail-line text-lg" />
									</div>
									<div className="text-left">
										<p
											className="font-bold text-sm"
											style={{ fontFamily: "'Sora', sans-serif" }}
										>
											Ver suscriptores
										</p>
										<p className="text-white/50 text-xs">
											{activeSubscribers} activos
										</p>
									</div>
								</button>
							</div>
						</div>

						{/* Portal Cleanup */}
						<div className="bg-white rounded-2xl border border-red-100 p-6">
							<h3
								className="text-red-700 font-bold text-sm mb-4"
								style={{ fontFamily: "'Sora', sans-serif" }}
							>
								<i className="ri-alert-line mr-1.5" />
								Mantenimiento del portal
							</h3>
							<p className="text-slate-400 text-xs mb-4">
								Esto eliminará{" "}
								<strong>
									todas las noticias, estadísticas, comentarios, reacciones,
									galería y reportes
								</strong>{" "}
								del portal. Solo usa esto si necesitas empezar desde cero.
							</p>
							{!showCleanupConfirm ? (
								<button
									onClick={() => setShowCleanupConfirm(true)}
									className="flex items-center gap-3 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors cursor-pointer group"
								>
									<div className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-lg group-hover:bg-white/20 transition-colors">
										<i className="ri-delete-bin-2-line text-lg" />
									</div>
									<div className="text-left">
										<p
											className="font-bold text-sm"
											style={{ fontFamily: "'Sora', sans-serif" }}
										>
											Vaciar todo el contenido
										</p>
										<p className="text-white/50 text-xs">
											Borrar noticias, stats y más
										</p>
									</div>
								</button>
							) : (
								<div className="bg-red-50 border border-red-200 rounded-xl p-4">
									<p className="text-red-700 text-sm font-semibold mb-3">
										¿Estás segura? Esto borrará todo el contenido del portal y
										no se puede deshacer.
									</p>
									<div className="flex gap-3">
										<button
											onClick={handleCleanupPortal}
											disabled={cleanupLoading}
											className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center gap-2"
										>
											{cleanupLoading ? (
												<i className="ri-loader-4-line animate-spin" />
											) : (
												<i className="ri-delete-bin-2-line" />
											)}
											Sí, vaciar todo
										</button>
										<button
											onClick={() => setShowCleanupConfirm(false)}
											className="px-5 py-2.5 border border-slate-200 text-slate-600 text-xs font-semibold rounded-lg hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
										>
											Cancelar
										</button>
									</div>
								</div>
							)}
						</div>

						{/* Recent articles */}
						<div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
							<div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
								<h3
									className="text-[#0B1F3A] font-bold text-sm"
									style={{ fontFamily: "'Sora', sans-serif" }}
								>
									Últimas noticias publicadas
								</h3>
								<button
									onClick={() => {
										setActiveTab("articles");
										setShowChangePassword(false);
									}}
									className="text-xs text-slate-400 hover:text-[#0B1F3A] transition-colors cursor-pointer"
								>
									Ver todas →
								</button>
							</div>
							<div className="divide-y divide-slate-50">
								{articles.slice(0, 5).map((article) => (
									<div
										key={article.id}
										className="px-6 py-3 flex items-center gap-3"
									>
										<div className="w-10 h-10 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
											<img
												src={article.image}
												alt=""
												className="w-full h-full object-cover object-top"
											/>
										</div>
										<div className="flex-1 min-w-0">
											<p
												className="text-[#0B1F3A] font-semibold text-xs truncate"
												style={{ fontFamily: "'Sora', sans-serif" }}
											>
												{article.title}
											</p>
											<p className="text-slate-400 text-[10px]">
												{article.category} · {article.date}
											</p>
										</div>
										<div className="flex items-center gap-1.5 flex-shrink-0">
											<button
												onClick={() => handleEdit(article)}
												className="w-7 h-7 flex items-center justify-center rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors cursor-pointer flex-shrink-0"
												title="Editar"
											>
												<i className="ri-pencil-line text-xs" />
											</button>
											{deleteConfirm === article.id ? (
												<div className="flex items-center gap-1">
													<button
														onClick={() => handleDelete(article.id)}
														className="px-2 py-1 bg-red-600 text-white text-[10px] font-bold rounded-md cursor-pointer whitespace-nowrap"
													>
														Confirmar
													</button>
													<button
														onClick={() => setDeleteConfirm(null)}
														className="px-2 py-1 bg-slate-200 text-slate-600 text-[10px] font-bold rounded-md cursor-pointer whitespace-nowrap"
													>
														Cancelar
													</button>
												</div>
											) : (
												<button
													onClick={() => setDeleteConfirm(article.id)}
													className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors cursor-pointer flex-shrink-0"
													title="Eliminar"
												>
													<i className="ri-delete-bin-line text-xs" />
												</button>
											)}
										</div>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>

				{/* ── ANALÍTICAS TAB ── */}
				<div className={activeTab === "analiticas" ? "block" : "hidden"}>
					{activeTab === "analiticas" && <AnaliticasTab />}
				</div>

				{/* ── ARTICLES TAB ── */}
				<div className={activeTab === "articles" ? "block" : "hidden"}>
					<div className="space-y-6">
						{/* Editor Form */}
						<div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
							<div className="bg-[#0B1F3A] px-6 py-4 flex items-center justify-between">
								<h2
									className="text-white font-bold text-sm"
									style={{ fontFamily: "'Sora', sans-serif" }}
								>
									{editingArticle ? "Editar Artículo" : "Nueva Noticia"}
								</h2>
								{editingArticle && (
									<button
										onClick={handleNew}
										className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
									>
										<i className="ri-add-line mr-1" /> Nueva noticia
									</button>
								)}
							</div>

							<form onSubmit={handleSave} className="px-6 py-6 space-y-5">
								<div>
									<label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
										Título *
									</label>
									<input
										type="text"
										value={formData.title}
										onChange={(e) =>
											setFormData({ ...formData, title: e.target.value })
										}
										placeholder="Ej: Arajet anuncia 8 nuevas rutas desde Santo Domingo"
										className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/30 focus:border-[#0B1F3A] transition-all"
										required
									/>
								</div>

								<div>
									<label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
										Categoría principal *
									</label>
									<select
										value={formData.primary_category}
										onChange={(e) => {
											const newPrimary = e.target.value;
											setFormData({
												...formData,
												primary_category: newPrimary,
												secondary_categories: formData.secondary_categories.filter(
													(c) => c !== newPrimary,
												),
												subcategory: formData.subcategory,
											});
										}}
										className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/30 focus:border-[#0B1F3A] transition-all cursor-pointer bg-white text-[#0B1F3A]"
									>
										<option value="">Seleccionar categoría principal...</option>
										{Object.keys(categoryColors).map((cat) => (
											<option key={cat} value={cat}>
												{cat}
											</option>
										))}
									</select>
									{formData.primary_category && (
										<p className="text-slate-400 text-[10px] mt-1.5">
											Badge principal:{" "}
											<span
												className={`inline-block text-white text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full ml-1 ${categoryColors[formData.primary_category] || "bg-slate-600"}`}
											>
												{formData.primary_category}
											</span>
										</p>
									)}
								</div>

								<div>
									<label className="block text-slate-400 font-semibold text-[10px] mb-2 uppercase tracking-wide">
										Categorías secundarias{" "}
										<span className="text-slate-300 font-normal normal-case text-[10px]">
											(opcional, menos destacadas)
										</span>
									</label>
									<div className="flex flex-wrap gap-1.5">
										{Object.keys(categoryColors)
											.filter((cat) => cat !== formData.primary_category)
											.map((cat) => {
												const selected = formData.secondary_categories.includes(cat);
												return (
													<button
														key={cat}
														type="button"
														onClick={() => {
															if (selected) {
																setFormData({
																	...formData,
																	secondary_categories: formData.secondary_categories.filter(
																		(c) => c !== cat,
																	),
																});
															} else {
																setFormData({
																	...formData,
																	secondary_categories: [
																		...formData.secondary_categories,
																		cat,
																	],
																});
															}
														}}
														className={`px-2.5 py-1.5 text-[11px] font-medium rounded-full border transition-all cursor-pointer whitespace-nowrap flex items-center gap-1 ${
															selected
																? "bg-slate-100 text-slate-700 border-slate-300"
																: "bg-white text-slate-400 border-slate-200 hover:border-slate-400 hover:text-slate-600"
														}`}
													>
														{selected && (
															<i className="ri-check-line text-[9px]" />
														)}
														{cat}
													</button>
												);
											})}
									</div>
									{formData.secondary_categories.length > 0 && (
										<p className="text-slate-400 text-[10px] mt-1.5">
											{formData.secondary_categories.length} categoría(s) secundaria(s)
										</p>
									)}
								</div>

								<div>
									<label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
										Fecha *
									</label>
									<input
										type="date"
										value={formData.date}
										onChange={(e) =>
											setFormData({ ...formData, date: e.target.value })
										}
										className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/30 focus:border-[#0B1F3A] transition-all"
										required
									/>
									<p className="text-slate-400 text-[10px] mt-1">
										Selecciona la fecha del calendario
									</p>
								</div>

								{/* Subcategory */}
								{(() => {
									const subcategoryOptions = ["Internacional"];
									if (formData.primary_category === "Turismo") {
										subcategoryOptions.push(...turismoSubcategories);
									}
									if (formData.subcategory && !subcategoryOptions.includes(formData.subcategory)) {
										subcategoryOptions.push(formData.subcategory);
									}
									return (
										<div>
											<label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
												Subcategoría
											</label>
											<select
												value={formData.subcategory}
												onChange={(e) =>
													setFormData({
														...formData,
														subcategory: e.target.value,
													})
												}
												className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/30 focus:border-[#0B1F3A] transition-all cursor-pointer bg-white"
											>
												<option value="">Sin subcategoría</option>
												{subcategoryOptions.map((sub) => (
													<option key={sub} value={sub}>
														{sub}
													</option>
												))}
											</select>
											<p className="text-slate-400 text-[10px] mt-1">
												Aparece como un badge secundario junto a la categoría principal en el card de la noticia.
											</p>
										</div>
									);
								})()}

								<ImageUploader
									value={formData.image}
									onChange={(url) =>
										setFormData((prev) => ({ ...prev, image: url }))
									}
									label="Imagen del artículo"
									folder="articles"
								/>

								{/* Imágenes adicionales */}
								<div>
									<label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
										Imágenes adicionales
									</label>
									{formData.additional_images.length > 0 && (
										<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
											{formData.additional_images.map((imgUrl, idx) => (
												<div key={idx} className="space-y-2">
													<div className="relative rounded-xl overflow-hidden border border-slate-200 bg-[#0B1F3A] aspect-video group">
														<img
															src={imgUrl}
															alt={`Imagen ${idx + 1}`}
															className="w-full h-full object-cover"
														/>
														<button
																type="button"
																onClick={() =>
																	setFormData((prev) => ({
																		...prev,
																		additional_images: prev.additional_images.filter(
																			(_, i) => i !== idx,
																		),
																		image_captions: prev.image_captions.filter(
																			(_, i) => i !== idx,
																		),
																	}))
																}
																className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center bg-white/90 hover:bg-white text-red-500 rounded-lg shadow-sm transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
																title="Eliminar imagen"
																>
																	<i className="ri-close-line text-sm" />
																</button>
														</div>
														<input
															type="text"
															value={formData.image_captions[idx] || ""}
															placeholder="Pie de foto (opcional)"
															onChange={(e) =>
																	setFormData((prev) => {
																		const newCaptions = [...prev.image_captions];
																		newCaptions[idx] = e.target.value;
																		return { ...prev, image_captions: newCaptions };
																	})
																}
															className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/30 focus:border-[#0B1F3A] transition-all"
															maxLength={120}
														/>
													</div>
												))}
											</div>
										)}
										{formData.additional_images.length < 8 && (
											<div className="border-2 border-dashed border-slate-200 rounded-xl p-4">
												<ImageUploader
													key={`additional-${formData.additional_images.length}`}
													value=""
													onChange={(url) => {
														if (url) {
															setFormData((prev) => ({
																...prev,
																additional_images: [
																	...prev.additional_images,
																	url,
																],
																image_captions: [...prev.image_captions, ""],
															}));
															}
														}}
													label=""
													folder="articles"
													crop={true}
												/>
											</div>
										)}
										<p className="text-slate-400 text-[10px] mt-1">
											{formData.additional_images.length}/8 imágenes · Estas aparecerán en la galería del artículo
										</p>
									</div>

									<VideoUploader
										value={formData.video_url}
										onChange={(url) =>
											setFormData({ ...formData, video_url: url })
										}
										label="Video de la noticia"
									/>
									<p className="text-slate-400 text-xs mt-1">
										Sube un video directo (max 50 MB) o pega una URL de YouTube.
									</p>

									<div>
										<label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
											Extracto *
										</label>
										<textarea
											value={formData.excerpt}
											onChange={(e) =>
												setFormData({ ...formData, excerpt: e.target.value })
											}
											placeholder="Breve resumen del artículo..."
											rows={4}
											className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/30 focus:border-[#0B1F3A] transition-all resize-none"
											maxLength={500}
										/>
										<p className="text-slate-400 text-[10px] mt-1">
											{formData.excerpt.length}/500 caracteres · Solo texto, sin formato
										</p>
									</div>

									<div>
										<label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
											Contenido completo *
										</label>
										<RichTextEditor
											value={formData.content}
											onChange={(html) => setFormData({ ...formData, content: html })}
											placeholder="Escribe el contenido de la noticia. Selecciona texto para formatear (negritas, viñetas) e inserta imágenes con la barra superior..."
											minHeight="360px"
											allowHtmlPaste={true}
										/>
										<p className="text-slate-400 text-[10px] mt-1">
											Da formato con la barra flotante (selecciona texto): negritas, itálica, títulos, citas y viñetas. Usa la barra superior para insertar imágenes (subiéndolas o por URL). También puedes pegar texto con formato desde Word.
										</p>
									</div>

									<div className="flex items-center gap-4 flex-wrap">
										<label className="flex items-center gap-2 cursor-pointer">
											<input
												type="checkbox"
												checked={formData.featured}
												onChange={(e) =>
													setFormData({ ...formData, featured: e.target.checked })
												}
												className="w-4 h-4 rounded border-slate-300 text-[#0B1F3A] focus:ring-[#0B1F3A]"
											/>
											<span className="text-sm text-slate-600">
												Marcar como destacado
											</span>
										</label>
										<label className="flex items-center gap-2 cursor-pointer">
											<input
												type="checkbox"
												checked={formData.is_international}
												onChange={(e) =>
													setFormData({
														...formData,
														is_international: e.target.checked,
													})
												}
												className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
											/>
											<span className="text-sm text-slate-600 flex items-center gap-1.5">
												<i className="ri-earth-line text-blue-500" />
												Noticia internacional
											</span>
										</label>
									</div>

									{/* Preview */}
									<div className="bg-slate-50 rounded-2xl border border-slate-200 p-5">
										<h4
											className="text-[#0B1F3A] font-bold text-sm mb-1"
											style={{ fontFamily: "'Sora', sans-serif" }}
										>
											<i className="ri-eye-line mr-1.5 text-blue-500" />
											Preview: ¿Cómo se verá esta noticia?
										</h4>
										<p className="text-slate-400 text-xs mb-4">
											Revisa cómo quedará antes de publicar. Los campos
											automáticos (autor, color, tiempo) se asignan al guardar.
										</p>
										<ArticlePreview
											data={{
												title: formData.title,
												excerpt: formData.excerpt,
												category: formData.primary_category || formData.secondary_categories[0] || "Sin categoría",
												category_color:
													categoryColors[formData.primary_category] ||
													categoryColors[formData.secondary_categories[0]] ||
													"bg-blue-500",
												secondary_categories: formData.secondary_categories,
												author: "Carmen Bretón",
												date: formData.date
													? formatDateForDisplay(formData.date)
													: "",
												read_time: formData.content
													? getReadTime(formData.content)
													: "4 min",
												image: formData.image,
												featured: formData.featured,
												slug: generateSlug(formData.title),
												content: formData.content,
											}}
										/>
									</div>

									<div className="flex gap-3">
										<button
											type="submit"
											disabled={saving}
											className="px-6 py-3 bg-[#0B1F3A] hover:bg-slate-800 text-white font-bold text-sm rounded-xl transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center gap-2"
											style={{ fontFamily: "'Sora', sans-serif" }}
										>
											{saving ? (
												<i className="ri-loader-4-line animate-spin" />
											) : (
												<i className="ri-save-line" />
											)}
											{editingArticle ? "Guardar cambios" : "Publicar noticia"}
										</button>
										{editingArticle && (
											<button
												type="button"
												onClick={handleNew}
												className="px-6 py-3 border border-slate-200 text-slate-600 font-semibold text-sm rounded-xl hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
											>
												Cancelar
											</button>
										)}
									</div>
								</form>
							</div>

							{/* Articles list */}
							<div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
								<div className="px-6 py-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
									<h2
										className="text-[#0B1F3A] font-bold text-sm"
										style={{ fontFamily: "'Sora', sans-serif" }}
									>
										Artículos publicados ({filteredArticles.length})
										<span className="block text-slate-400 text-xs font-normal mt-0.5">
											<i className="ri-arrow-up-down-line" /> Usa ↑/↓ para mover una noticia. <i className="ri-pushpin-2-line" /> Fijar la deja siempre arriba, aunque publiques nuevas.
										</span>
									</h2>
									<div className="flex gap-3 items-center flex-wrap">
										{uniqueCategories.length > 0 && (
											<select
												value={categoryFilter}
												onChange={(e) => setCategoryFilter(e.target.value)}
												className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/30 focus:border-[#0B1F3A] transition-all bg-white cursor-pointer text-slate-700"
											>
												<option value="all">Todas las categorías</option>
												{uniqueCategories.map((cat) => {
													const isOrphan = !standardCategories.includes(cat);
													return (
														<option key={cat} value={cat}>
															{cat}
															{isOrphan ? " ⚠️" : ""}
														</option>
													);
												})}
											</select>
										)}
										{categoryFilter === "Turismo" && (
											<select
												value={subcategoryFilter}
												onChange={(e) => setSubcategoryFilter(e.target.value)}
												className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/30 focus:border-[#0B1F3A] transition-all bg-white cursor-pointer text-slate-700"
											>
												<option value="all">Todas las subcategorías</option>
												{turismoSubcategories.map((sub) => (
													<option key={sub} value={sub}>
														{sub}
													</option>
												))}
											</select>
										)}
										<div className="relative">
											<i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
											<input
												type="text"
												value={searchTerm}
												onChange={(e) => setSearchTerm(e.target.value)}
												placeholder="Buscar por título, contenido, autor, etiqueta..."
												className="pl-9 pr-4 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/30 focus:border-[#0B1F3A] transition-all w-full sm:w-64"
											/>
										</div>
									</div>
								</div>

								{loadingArticles ? (
									<div className="p-6 space-y-3">
										{[1, 2, 3].map((i) => (
											<div
												key={i}
												className="h-16 bg-slate-100 rounded-xl animate-pulse"
											/>
										))}
									</div>
								) : filteredArticles.length === 0 ? (
									<div className="text-center py-10">
										<p className="text-slate-400 text-sm">
											No se encontraron artículos.
										</p>
									</div>
								) : (
									<div className="divide-y divide-slate-50">
										{sortedArticles.map((article) => (
											<div
												key={article.id}
												className="px-6 py-4 flex items-center gap-4 hover:bg-slate-50 transition-colors"
											>
												<div className="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-slate-100">
													<img
														src={article.image}
														alt=""
														className="w-full h-full object-cover object-top"
													/>
												</div>
												<div className="flex-1 min-w-0">
													<div className="flex items-center gap-2 mb-0.5 flex-wrap">
														<span
															className={`inline-block text-white text-[9px] font-bold tracking-widest uppercase px-1.5 py-0.5 rounded-full ${
																article.category_color && standardCategories.includes(article.category)
																	? article.category_color
																	: "bg-slate-400"
															}`}
														>
															{article.category || "Sin categoría"}
														</span>
														{!standardCategories.includes(article.category) && article.category && (
															<span className="inline-block text-amber-700 text-[9px] font-bold tracking-widest uppercase px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-100">
																<i className="ri-alert-line mr-0.5" />
																Categoría no estándar
															</span>
														)}
														{article.id === latestId && (
															<span className="inline-flex items-center gap-1 text-emerald-700 text-[9px] font-bold tracking-widest uppercase px-1.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-100">
																<i className="ri-flashlight-fill" />
																Última subida · sale primero
															</span>
														)}
														{isPinned(article) && (
															<span className="inline-flex items-center gap-1 text-violet-700 text-[9px] font-bold tracking-widest uppercase px-1.5 py-0.5 rounded-full bg-violet-50 border border-violet-100">
																<i className="ri-pushpin-2-fill" />
																Posición {pinnedIds.indexOf(article.id) + 1}
															</span>
														)}
														{article.featured && (
															<span className="inline-block text-amber-600 text-[9px] font-bold tracking-widest uppercase px-1.5 py-0.5 rounded-full bg-amber-50">
																<i className="ri-star-line mr-0.5" />
																Destacado
															</span>
														)}
														{article.is_international && (
															<span className="inline-flex items-center gap-1 text-blue-600 text-[9px] font-bold tracking-widest uppercase px-1.5 py-0.5 rounded-full bg-blue-50 border border-blue-100">
																<i className="ri-earth-line" />
																Internacional
															</span>
														)}
													</div>
													<p
														className="text-[#0B1F3A] font-semibold text-sm truncate"
														style={{ fontFamily: "'Sora', sans-serif" }}
													>
														{article.title}
													</p>
													<p className="text-slate-400 text-xs">
														{article.date} · {article.read_time}
													</p>
												</div>
												<div className="flex items-center gap-2 flex-shrink-0">
													{isPinned(article) ? (
														<>
															<button
																onClick={() => handleMovePinned(article.id, -1)}
																disabled={saving || pinnedIds[0] === article.id}
																className="w-8 h-8 flex items-center justify-center rounded-lg bg-violet-50 text-violet-600 hover:bg-violet-100 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
																title="Subir posición"
															>
																<i className="ri-arrow-up-line text-sm" />
															</button>
															<button
																onClick={() => handleMovePinned(article.id, 1)}
																disabled={saving || pinnedIds[pinnedIds.length - 1] === article.id}
																className="w-8 h-8 flex items-center justify-center rounded-lg bg-violet-50 text-violet-600 hover:bg-violet-100 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
																title="Bajar posición"
															>
																<i className="ri-arrow-down-line text-sm" />
															</button>
															<button
																onClick={() => handleUnpin(article.id)}
																disabled={saving}
																className="w-8 h-8 flex items-center justify-center rounded-lg bg-violet-600 text-white hover:bg-violet-700 transition-colors cursor-pointer disabled:opacity-60"
																title="Desfijar (volver al orden por fecha)"
															>
																<i className="ri-unpin-line text-sm" />
															</button>
														</>
													) : (
														<>
														<button
															onClick={() => handleMoveFree(article.id, -1)}
															disabled={saving || article.id === latestId}
															className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors cursor-pointer disabled:opacity-60"
															title="Subir"
														>
															<i className="ri-arrow-up-line text-sm" />
														</button>
														<button
															onClick={() => handleMoveFree(article.id, 1)}
															disabled={saving || article.id === latestId}
															className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors cursor-pointer disabled:opacity-60"
															title="Bajar"
														>
															<i className="ri-arrow-down-line text-sm" />
														</button>
														<button
															onClick={() => handlePin(article.id)}
															disabled={saving}
															className="w-8 h-8 flex items-center justify-center rounded-lg bg-violet-50 text-violet-600 hover:bg-violet-100 transition-colors cursor-pointer disabled:opacity-60"
															title="Fijar posición (sale antes que las noticias por fecha)"
														>
															<i className="ri-pushpin-2-line text-sm" />
														</button>
														</>
													)}
													<Link
														to={`/articulo/${article.slug}`}
														target="_blank"
														className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors cursor-pointer"
														title="Ver artículo"
													>
														<i className="ri-eye-line text-sm" />
													</Link>
													<button
														onClick={() => handleEdit(article)}
														className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors cursor-pointer"
														title="Editar"
													>
														<i className="ri-pencil-line text-sm" />
													</button>
													{deleteConfirm === article.id ? (
														<div className="flex items-center gap-1">
															<button
																onClick={() => handleDelete(article.id)}
																className="px-2 py-1 bg-red-600 text-white text-[10px] font-bold rounded-md cursor-pointer whitespace-nowrap"
															>
																Confirmar
															</button>
															<button
																onClick={() => setDeleteConfirm(null)}
																className="px-2 py-1 bg-slate-200 text-slate-600 text-[10px] font-bold rounded-md cursor-pointer whitespace-nowrap"
															>
																Cancelar
															</button>
														</div>
													) : (
														<button
															onClick={() => setDeleteConfirm(article.id)}
															className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors cursor-pointer"
															title="Eliminar"
														>
															<i className="ri-delete-bin-line text-sm" />
														</button>
													)}
												</div>
											</div>
										))}
									</div>
								)}
							</div>
						</div>
				</div>

				{/* ── SPONSORS TAB ── */}
				<div className={activeTab === "sponsors" ? "block" : "hidden"}>
					<div className="space-y-6">
						{sponsorMessage && (
							<div
								className={`flex items-center gap-2 rounded-xl px-4 py-3 ${
									sponsorMessage.includes("Error")
										? "bg-red-50 border border-red-200 text-red-600"
										: "bg-emerald-50 border border-emerald-200 text-emerald-600"
								}`}
							>
								<i
									className={
										sponsorMessage.includes("Error")
											? "ri-error-warning-line"
											: "ri-check-line"
									}
								/>
								<p className="text-sm font-semibold">{sponsorMessage}</p>
							</div>
						)}

						{/* Sponsor form */}
						{showSponsorForm && (
							<div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
								<div className="bg-red-600 px-6 py-4 flex items-center justify-between">
									<h2
										className="text-white font-bold text-sm"
										style={{ fontFamily: "'Sora', sans-serif" }}
									>
										{editingSponsor
											? "Editar Sponsor"
											: "Nuevo Sponsor / Publicidad"}
									</h2>
									<button
										onClick={() => {
											setShowSponsorForm(false);
											setEditingSponsor(null);
										}}
										className="w-7 h-7 flex items-center justify-center bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors cursor-pointer"
									>
										<i className="ri-close-line text-sm" />
									</button>
								</div>

								<form
									onSubmit={handleSaveSponsor}
									className="px-6 py-6 space-y-5"
								>
									<div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
										<div>
											<label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
												Empresa / Marca *
											</label>
											<input
												type="text"
												value={sponsorForm.company_name}
												onChange={(e) =>
													setSponsorForm({
														...sponsorForm,
														company_name: e.target.value,
													})
												}
												placeholder="Nombre de la empresa"
												className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-500 transition-all"
												required
											/>
										</div>
										<div>
											<label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
												Sitio web
											</label>
											<input
												type="url"
												value={sponsorForm.website_url}
												onChange={(e) =>
													setSponsorForm({
														...sponsorForm,
														website_url: e.target.value,
													})
												}
												placeholder="https://empresa.com"
												className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-500 transition-all"
											/>
										</div>
									</div>

									<div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
										<ImageUploader
											value={sponsorForm.logo_url}
											onChange={(url) =>
												setSponsorForm((prev) => ({ ...prev, logo_url: url }))
											}
											label="Logo del sponsor"
											folder="sponsors"
										/>
										<ImageUploader
											value={sponsorForm.banner_url}
											onChange={(url) =>
												setSponsorForm((prev) => ({ ...prev, banner_url: url }))
											}
											label="Banner publicitario"
											maxSizeMB={BANNER_MAX_MB}
											keepGif
											helper={<BannerSpecs adType={sponsorForm.ad_type} />}
											folder="sponsors"
										/>
									</div>

									<div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
										<div>
											<label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
												Nombre de contacto
											</label>
											<input
												type="text"
												value={sponsorForm.contact_name}
												onChange={(e) =>
													setSponsorForm({
														...sponsorForm,
														contact_name: e.target.value,
													})
												}
												placeholder="Nombre del contacto"
												className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-500 transition-all"
											/>
										</div>
										<div>
											<label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
												Email de contacto
											</label>
											<input
												type="email"
												value={sponsorForm.contact_email}
												onChange={(e) =>
													setSponsorForm({
														...sponsorForm,
														contact_email: e.target.value,
													})
												}
												placeholder="contacto@empresa.com"
												className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-500 transition-all"
											/>
										</div>
										<div>
											<label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
												Teléfono
											</label>
											<input
												type="text"
												value={sponsorForm.contact_phone}
												onChange={(e) =>
													setSponsorForm({
														...sponsorForm,
														contact_phone: e.target.value,
													})
												}
												placeholder="+1 809 000 0000"
												className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-500 transition-all"
											/>
										</div>
									</div>

									<div className="grid grid-cols-1 sm:grid-cols-4 gap-5">
										<div>
											<label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
												Tamaño / tipo de anuncio
											</label>
											<select
												value={sponsorForm.ad_type}
												onChange={(e) =>
													setSponsorForm({
														...sponsorForm,
														ad_type: e.target.value,
													})
												}
												className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-500 transition-all cursor-pointer bg-white"
											>
												<option value="cintillo">Cintillo horizontal · 728 × 90 px</option>
												<option value="mediano">Banner mediano · 300 × 250 px</option>
												<option value="banner">Banner pequeño · 300 × 125 px</option>
												<option value="sidebar">Sidebar</option>
												<option value="hero">Hero</option>
												<option value="newsletter">Newsletter</option>
												<option value="sponsored_article">
													Artículo patrocinado
												</option>
											</select>
										</div>
										<div className="sm:col-span-3">
											<label className="block text-[#0B1F3A] font-semibold text-xs mb-3 uppercase tracking-wide">
												¿En qué secciones quieres que se vea? (elige una o más)
											</label>
											<div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
												{[
													{ value: "home", label: "Inicio (Home)" },
													{ value: "actualidad", label: "Actualidad" },
													{ value: "aerolineas", label: "Aerolíneas" },
													{ value: "aeropuertos", label: "Aeropuertos" },
													{ value: "turismo", label: "Turismo" },
													{ value: "estadisticas", label: "Estadísticas" },
													{ value: "aviacion-rd", label: "Aviación RD" },
													{ value: "articulo", label: "Artículos (interno)" },
													{ value: "todas", label: "Todas las secciones" },
												].map((opt) => {
													const placements: string[] = (() => {
														try {
															return JSON.parse(sponsorForm.placement || "[]");
														} catch {
															return sponsorForm.placement
																? [sponsorForm.placement]
																: [];
														}
													})();
													const isChecked = placements.includes(opt.value);
													return (
														<label
															key={opt.value}
															className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-slate-50 transition-colors border border-slate-100"
														>
															<input
																type="checkbox"
																checked={isChecked}
																onChange={(e) => {
																	let current: string[] = [];
																	try {
																		current = JSON.parse(
																			sponsorForm.placement || "[]",
																		);
																	} catch {
																		current = sponsorForm.placement
																			? [sponsorForm.placement]
																			: [];
																	}
																	const newPlacements = e.target.checked
																		? [
																				...current.filter(
																					(p) => p !== opt.value,
																				),
																				opt.value,
																			]
																		: current.filter((p) => p !== opt.value);
																	setSponsorForm({
																		...sponsorForm,
																		placement: JSON.stringify(newPlacements),
																	});
																}}
																className="w-4 h-4 rounded border-slate-300 text-red-600"
															/>
															<span className="text-sm text-slate-700">
																{opt.label}
															</span>
														</label>
													);
												})}
											</div>
											<p className="text-slate-400 text-[10px] mt-2">
												Cuantas más secciones elija el anunciante, mayor
												visibilidad y costo.
											</p>
										</div>
										<div>
											<label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
												Fecha inicio
											</label>
											<input
												type="text"
												value={sponsorForm.start_date}
												onChange={(e) =>
													setSponsorForm({
														...sponsorForm,
														start_date: e.target.value,
													})
												}
												placeholder="01 May 2026"
												className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-500 transition-all"
											/>
										</div>
										<div>
											<label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
												Fecha fin
											</label>
											<input
												type="text"
												value={sponsorForm.end_date}
												onChange={(e) =>
													setSponsorForm({
														...sponsorForm,
														end_date: e.target.value,
													})
												}
												placeholder="31 May 2026"
												className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-500 transition-all"
											/>
										</div>
									</div>

									<div>
										<label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
											Notas internas
										</label>
										<textarea
											value={sponsorForm.notes}
											onChange={(e) =>
												setSponsorForm({
													...sponsorForm,
													notes: e.target.value,
												})
											}
											placeholder="Notas sobre el acuerdo, precio, condiciones..."
											rows={3}
											className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-500 transition-all resize-none"
										/>
									</div>

									<div className="flex items-center gap-3">
										<label className="flex items-center gap-2 cursor-pointer">
											<input
												type="checkbox"
												checked={sponsorForm.active}
												onChange={(e) =>
													setSponsorForm({
														...sponsorForm,
														active: e.target.checked,
													})
												}
												className="w-4 h-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
											/>
											<span className="text-sm text-slate-600">
												Sponsor activo (visible en el portal)
											</span>
										</label>
									</div>

									<div className="flex gap-3">
										<button
											type="submit"
											disabled={saving}
											className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-bold text-sm rounded-xl transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center gap-2"
											style={{ fontFamily: "'Sora', sans-serif" }}
										>
											{saving ? (
												<i className="ri-loader-4-line animate-spin" />
											) : (
												<i className="ri-save-line" />
											)}
											{editingSponsor ? "Guardar cambios" : "Agregar sponsor"}
										</button>
										<button
											type="button"
											onClick={() => {
												setShowSponsorForm(false);
												setEditingSponsor(null);
											}}
											className="px-6 py-3 border border-slate-200 text-slate-600 font-semibold text-sm rounded-xl hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
										>
											Cancelar
										</button>
									</div>
								</form>
							</div>
						)}

						{/* Sponsors list */}
						<div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
							<div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
								<h2
									className="text-[#0B1F3A] font-bold text-sm"
									style={{ fontFamily: "'Sora', sans-serif" }}
								>
									Sponsors y anunciantes ({sponsors.length})
								</h2>
								{!showSponsorForm && (
									<button
										onClick={handleNewSponsor}
										className="flex items-center gap-1.5 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
										style={{ fontFamily: "'Sora', sans-serif" }}
									>
										<i className="ri-add-line" /> Nuevo sponsor
									</button>
								)}
							</div>

							{loadingSponsors ? (
								<div className="p-6 space-y-3">
									{[1, 2].map((i) => (
										<div
											key={i}
											className="h-20 bg-slate-100 rounded-xl animate-pulse"
										/>
									))}
								</div>
							) : sponsors.length === 0 ? (
								<div className="text-center py-12">
									<div className="w-12 h-12 flex items-center justify-center bg-slate-100 rounded-xl mx-auto mb-3">
										<i className="ri-megaphone-line text-slate-400 text-2xl" />
									</div>
									<p className="text-slate-400 text-sm mb-3">
										No hay sponsors registrados aún.
									</p>
									<button
										onClick={handleNewSponsor}
										className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
									>
										Agregar primer sponsor
									</button>
								</div>
							) : (
								<div className="divide-y divide-slate-50">
									{sponsors.map((sponsor) => (
										<div
											key={sponsor.id}
											className="px-6 py-4 flex items-center gap-4 hover:bg-slate-50 transition-colors"
										>
											<div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0 flex items-center justify-center">
												{sponsor.logo_url ? (
													<img
														src={sponsor.logo_url}
														alt={sponsor.company_name}
														className="w-full h-full object-contain p-1"
													/>
												) : (
													<i className="ri-building-line text-slate-400 text-xl" />
												)}
											</div>
											<div className="flex-1 min-w-0">
												<div className="flex items-center gap-2 mb-0.5">
													<p
														className="text-[#0B1F3A] font-bold text-sm"
														style={{ fontFamily: "'Sora', sans-serif" }}
													>
														{sponsor.company_name}
													</p>
													<span
														className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full ${
															sponsor.active
																? "bg-emerald-50 text-emerald-600"
																: "bg-slate-100 text-slate-400"
														}`}
													>
														{sponsor.active ? "Activo" : "Inactivo"}
													</span>
												</div>
												<p className="text-slate-400 text-xs">
													{sponsor.ad_type} · {sponsor.placement}
													{sponsor.contact_email &&
														` · ${sponsor.contact_email}`}
												</p>
												{(sponsor.start_date || sponsor.end_date) && (
													<p className="text-slate-300 text-[10px]">
														{sponsor.start_date} → {sponsor.end_date}
													</p>
												)}
											</div>
											<div className="flex items-center gap-2 flex-shrink-0">
												{sponsor.website_url && (
													<a
														href={sponsor.website_url}
														target="_blank"
														rel="nofollow noreferrer"
														className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors cursor-pointer"
														title="Ver sitio web"
													>
														<i className="ri-external-link-line text-sm" />
													</a>
												)}
												<button
													onClick={() => handleEditSponsor(sponsor)}
													className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors cursor-pointer"
													title="Editar"
												>
													<i className="ri-pencil-line text-sm" />
												</button>
												{deleteSponsorConfirm === sponsor.id ? (
													<div className="flex items-center gap-1">
														<button
															onClick={() => handleDeleteSponsor(sponsor.id)}
															className="px-2 py-1 bg-red-600 text-white text-[10px] font-bold rounded-md cursor-pointer whitespace-nowrap"
														>
															Confirmar
														</button>
														<button
															onClick={() => setDeleteSponsorConfirm(null)}
															className="px-2 py-1 bg-slate-200 text-slate-600 text-[10px] font-bold rounded-md cursor-pointer whitespace-nowrap"
														>
															Cancelar
														</button>
													</div>
												) : (
													<button
														onClick={() => setDeleteSponsorConfirm(sponsor.id)}
														className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors cursor-pointer"
														title="Eliminar"
													>
														<i className="ri-delete-bin-line text-sm" />
													</button>
												)}
											</div>
										</div>
									))}
								</div>
							)}
						</div>
					</div>
				</div>

				{/* ── NEWSLETTER TAB ── */}
				<div className={activeTab === "newsletter" ? "block" : "hidden"}>
					<NewsletterTab subscribers={subscribers} loading={loadingSubscribers} />
				</div>

				{/* ── MESSAGES TAB ── */}
				<div className={activeTab === "messages" ? "block" : "hidden"}>
					<div className="space-y-6">
						<div>
							<h2
								className="text-[#0B1F3A] font-extrabold text-2xl mb-1"
								style={{ fontFamily: "'Sora', sans-serif" }}
							>
								Mensajes recibidos
							</h2>
							<p className="text-slate-500 text-sm">
								Mensajes enviados a través del formulario de contacto.
							</p>
						</div>

						{/* Stats */}
						<div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
							<div className="bg-white rounded-xl border border-slate-100 p-5 text-center">
								<div className="w-10 h-10 flex items-center justify-center bg-blue-50 rounded-xl mx-auto mb-3">
									<i className="ri-message-3-line text-blue-600 text-xl" />
								</div>
								<p
									className="text-[#0B1F3A] font-extrabold text-3xl mb-1"
									style={{ fontFamily: "'Sora', sans-serif" }}
								>
									{messages.length}
								</p>
								<p className="text-slate-400 text-xs">Total mensajes</p>
							</div>
							<div className="bg-white rounded-xl border border-slate-100 p-5 text-center">
								<div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-xl mx-auto mb-3">
									<i className="ri-mail-unread-line text-amber-600 text-xl" />
								</div>
								<p
									className="text-[#0B1F3A] font-extrabold text-3xl mb-1"
									style={{ fontFamily: "'Sora', sans-serif" }}
								>
									{messages.filter((m) => !m.read).length}
								</p>
								<p className="text-slate-400 text-xs">Sin leer</p>
							</div>
							<div className="bg-white rounded-xl border border-slate-100 p-5 text-center">
								<div className="w-10 h-10 flex items-center justify-center bg-emerald-50 rounded-xl mx-auto mb-3">
									<i className="ri-mail-open-line text-emerald-600 text-xl" />
								</div>
								<p
									className="text-[#0B1F3A] font-extrabold text-3xl mb-1"
									style={{ fontFamily: "'Sora', sans-serif" }}
								>
									{messages.filter((m) => m.read).length}
								</p>
								<p className="text-slate-400 text-xs">Leídos</p>
							</div>
						</div>

						{/* Filters */}
						<div className="bg-white rounded-xl border border-slate-100 p-4 flex flex-wrap gap-2">
							{[
								{ id: "all", label: "Todos" },
								{ id: "unread", label: "Sin leer" },
								{ id: "read", label: "Leídos" },
							].map((f) => (
								<button
									key={f.id}
									onClick={() => {
										setMessageFilter(f.id);
										setSelectedMessage(null);
									}}
									className={`px-4 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
										messageFilter === f.id
											? "bg-[#0B1F3A] text-white"
											: "bg-slate-50 text-slate-600 hover:bg-slate-100"
									}`}
								>
									{f.label}
								</button>
							))}
						</div>

						{/* Messages list */}
						<div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
							<div className="px-6 py-4 border-b border-slate-100">
								<h2
									className="text-[#0B1F3A] font-bold text-sm"
									style={{ fontFamily: "'Sora', sans-serif" }}
								>
									Lista de mensajes
								</h2>
							</div>

							{loadingMessages ? (
								<div className="p-6 space-y-3">
									{[1, 2, 3].map((i) => (
										<div
											key={i}
											className="h-16 bg-slate-100 rounded-xl animate-pulse"
										/>
									))}
								</div>
							) : messages.length === 0 ? (
								<div className="text-center py-12">
									<div className="w-12 h-12 flex items-center justify-center bg-slate-100 rounded-xl mx-auto mb-3">
										<i className="ri-message-3-line text-slate-400 text-2xl" />
									</div>
									<p className="text-slate-400 text-sm">
										Aún no hay mensajes recibidos.
									</p>
									<p className="text-slate-300 text-xs mt-1">
										Los mensajes enviados desde la página de contacto aparecerán
										aquí.
									</p>
								</div>
							) : (
								<div className="divide-y divide-slate-50">
									{(messageFilter === "all"
										? messages
										: messageFilter === "unread"
											? messages.filter((m) => !m.read)
											: messages.filter((m) => m.read)
									).map((msg) => (
										<div key={msg.id}>
											<div
												className={`px-6 py-4 flex items-start gap-4 cursor-pointer hover:bg-slate-50 transition-colors ${
													!msg.read ? "bg-blue-50/30" : ""
												}`}
												onClick={() => {
													setSelectedMessage(
														selectedMessage === msg.id ? null : msg.id,
													);
													if (!msg.read) {
														supabase
															.from("contact_messages")
															.update({ read: true })
															.eq("id", msg.id)
															.then(() => {
																setMessages((prev) =>
																	prev.map((m) =>
																		m.id === msg.id ? { ...m, read: true } : m,
																	),
																);
															});
													}
												}}
											>
												<div
													className={`w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 ${
														!msg.read ? "bg-blue-100" : "bg-slate-100"
													}`}
												>
													<i
														className={`ri-message-3-line ${!msg.read ? "text-blue-600" : "text-slate-400"} text-sm`}
													/>
												</div>
												<div className="flex-1 min-w-0">
													<div className="flex items-center gap-2 mb-0.5">
														<p
															className="text-[#0B1F3A] font-semibold text-sm truncate"
															style={{ fontFamily: "'Sora', sans-serif" }}
														>
															{msg.name}
														</p>
														{!msg.read && (
															<span className="inline-block w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
														)}
													</div>
													<p className="text-slate-400 text-xs">
														{msg.tipo_consulta} · {msg.email}
													</p>
													<p className="text-slate-500 text-xs mt-1 truncate">
														{msg.asunto}
													</p>
												</div>
												<div className="text-slate-300 text-[10px] whitespace-nowrap flex-shrink-0 mt-1">
													{msg.created_at
														? new Date(msg.created_at).toLocaleDateString(
																"es-DO",
																{
																	day: "2-digit",
																	month: "short",
																	year: "numeric",
																},
															)
														: "—"}
												</div>
											</div>
											{selectedMessage === msg.id && (
												<div className="px-6 pb-4">
													<div className="ml-[52px] bg-slate-50 rounded-xl border border-slate-100 p-5">
														<div className="flex items-center gap-2 mb-2">
															<span
																className="text-[#0B1F3A] font-bold text-sm"
																style={{ fontFamily: "'Sora', sans-serif" }}
															>
																{msg.asunto}
															</span>
															<span
																className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full ${
																	msg.read
																		? "bg-slate-100 text-slate-500"
																		: "bg-blue-100 text-blue-600"
																}`}
															>
																{msg.read ? "Leído" : "Sin leer"}
															</span>
														</div>
														<p className="text-slate-400 text-xs mb-3">
															{msg.name} · {msg.email} · {msg.tipo_consulta}
														</p>
														<p className="text-slate-600 text-sm leading-relaxed whitespace-pre-wrap">
															{msg.mensaje}
														</p>
														<div className="flex items-center gap-2 mt-3">
															<a
																href={`mailto:${msg.email}`}
																className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
															>
																<i className="ri-reply-line" /> Responder
															</a>
															<button
																onClick={async (e) => {
																	e.stopPropagation();
																	await supabase
																		.from("contact_messages")
																		.delete()
																		.eq("id", msg.id);
																	setMessages((prev) =>
																		prev.filter((m) => m.id !== msg.id),
																	);
																	setSelectedMessage(null);
																}}
																className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-bold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
															>
																<i className="ri-delete-bin-line" /> Eliminar
															</button>
														</div>
													</div>
												</div>
											)}
										</div>
									))}
								</div>
							)}
						</div>
					</div>
				</div>

				{/* ── PUBLICIDAD TAB ── */}
				<div className={activeTab === "publicidad" ? "block" : "hidden"}>
					<div className="space-y-6">
						<div>
							<h2
								className="text-[#0B1F3A] font-extrabold text-2xl mb-1"
								style={{ fontFamily: "'Sora', sans-serif" }}
							>
								Solicitudes de publicidad
							</h2>
							<p className="text-slate-500 text-sm">
								Formularios enviados desde la pagina de publicidad.
							</p>
						</div>

						{/* Stats */}
						<div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
							<div className="bg-white rounded-xl border border-slate-100 p-5 text-center">
								<div className="w-10 h-10 flex items-center justify-center bg-orange-50 rounded-xl mx-auto mb-3">
									<i className="ri-advertisement-line text-orange-600 text-xl" />
								</div>
								<p
									className="text-[#0B1F3A] font-extrabold text-3xl mb-1"
									style={{ fontFamily: "'Sora', sans-serif" }}
								>
									{adRequests.length}
								</p>
								<p className="text-slate-400 text-xs">Total solicitudes</p>
							</div>
							<div className="bg-white rounded-xl border border-slate-100 p-5 text-center">
								<div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-xl mx-auto mb-3">
									<i className="ri-mail-unread-line text-amber-600 text-xl" />
								</div>
								<p
									className="text-[#0B1F3A] font-extrabold text-3xl mb-1"
									style={{ fontFamily: "'Sora', sans-serif" }}
								>
									{adRequests.filter((m) => !m.read).length}
								</p>
								<p className="text-slate-400 text-xs">Sin leer</p>
							</div>
							<div className="bg-white rounded-xl border border-slate-100 p-5 text-center">
								<div className="w-10 h-10 flex items-center justify-center bg-emerald-50 rounded-xl mx-auto mb-3">
									<i className="ri-mail-open-line text-emerald-600 text-xl" />
								</div>
								<p
									className="text-[#0B1F3A] font-extrabold text-3xl mb-1"
									style={{ fontFamily: "'Sora', sans-serif" }}
								>
									{adRequests.filter((m) => m.read).length}
								</p>
								<p className="text-slate-400 text-xs">Leidas</p>
							</div>
						</div>

						{/* Filters */}
						<div className="bg-white rounded-xl border border-slate-100 p-4 flex flex-wrap gap-2">
							{[
								{ id: "all", label: "Todas" },
								{ id: "unread", label: "Sin leer" },
								{ id: "read", label: "Leidas" },
							].map((f) => (
								<button
									key={f.id}
									onClick={() => {
										setAdRequestFilter(f.id);
										setSelectedAdRequest(null);
									}}
									className={`px-4 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
										adRequestFilter === f.id
											? "bg-[#0B1F3A] text-white"
											: "bg-slate-50 text-slate-600 hover:bg-slate-100"
									}`}
								>
									{f.label}
								</button>
							))}
						</div>

						{/* Requests list */}
						<div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
							<div className="px-6 py-4 border-b border-slate-100">
								<h2
									className="text-[#0B1F3A] font-bold text-sm"
									style={{ fontFamily: "'Sora', sans-serif" }}
								>
									Lista de solicitudes
								</h2>
							</div>

							{loadingAdRequests ? (
								<div className="p-6 space-y-3">
									{[1, 2, 3].map((i) => (
										<div
											key={i}
											className="h-16 bg-slate-100 rounded-xl animate-pulse"
										/>
									))}
								</div>
							) : adRequests.length === 0 ? (
								<div className="text-center py-12">
									<div className="w-12 h-12 flex items-center justify-center bg-slate-100 rounded-xl mx-auto mb-3">
										<i className="ri-advertisement-line text-slate-400 text-2xl" />
									</div>
									<p className="text-slate-400 text-sm">
										Aun no hay solicitudes de publicidad.
									</p>
									<p className="text-slate-300 text-xs mt-1">
										Las solicitudes enviadas desde la pagina de publicidad
										apareceran aqui.
									</p>
								</div>
							) : (
								<div className="divide-y divide-slate-50">
									{(adRequestFilter === "all"
										? adRequests
										: adRequestFilter === "unread"
											? adRequests.filter((m) => !m.read)
											: adRequests.filter((m) => m.read)
									).map((req) => (
										<div key={req.id}>
											<div
												className={`px-6 py-4 flex items-start gap-4 cursor-pointer hover:bg-slate-50 transition-colors ${
													!req.read ? "bg-orange-50/30" : ""
												}`}
												onClick={() => {
													setSelectedAdRequest(
														selectedAdRequest === req.id ? null : req.id,
													);
													if (!req.read) {
														supabase
															.from("advertising_requests")
															.update({ read: true })
															.eq("id", req.id)
															.then(() => {
																setAdRequests((prev) =>
																	prev.map((m) =>
																		m.id === req.id ? { ...m, read: true } : m,
																	),
																);
															});
													}
												}}
											>
												<div
													className={`w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 ${
														!req.read ? "bg-orange-100" : "bg-slate-100"
													}`}
												>
													<i
														className={`ri-building-line ${!req.read ? "text-orange-600" : "text-slate-400"} text-sm`}
													/>
												</div>
												<div className="flex-1 min-w-0">
													<div className="flex items-center gap-2 mb-0.5">
														<p
															className="text-[#0B1F3A] font-semibold text-sm truncate"
															style={{ fontFamily: "'Sora', sans-serif" }}
														>
															{req.empresa}
														</p>
														{!req.read && (
															<span className="inline-block w-2 h-2 rounded-full bg-orange-500 flex-shrink-0" />
														)}
													</div>
													<p className="text-slate-400 text-xs">
														{req.tipo_espacio} · {req.email}
													</p>
													<p className="text-slate-500 text-xs mt-1 truncate">
														{req.name}
													</p>
												</div>
												<div className="text-slate-300 text-[10px] whitespace-nowrap flex-shrink-0 mt-1">
													{req.created_at
														? new Date(req.created_at).toLocaleDateString(
																"es-DO",
																{
																	day: "2-digit",
																	month: "short",
																	year: "numeric",
																},
															)
														: "—"}
												</div>
											</div>
											{selectedAdRequest === req.id && (
												<div className="px-6 pb-4">
													<div className="ml-[52px] bg-slate-50 rounded-xl border border-slate-100 p-5">
														<div className="flex items-center gap-2 mb-2">
															<span
																className="text-[#0B1F3A] font-bold text-sm"
																style={{ fontFamily: "'Sora', sans-serif" }}
															>
																{req.empresa}
															</span>
															<span
																className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full ${
																	req.read
																		? "bg-slate-100 text-slate-500"
																		: "bg-orange-100 text-orange-600"
																}`}
															>
																{req.read ? "Leida" : "Sin leer"}
															</span>
														</div>
														<p className="text-slate-400 text-xs mb-3">
															{req.name} · {req.email} ·{" "}
															{req.telefono || "Sin telefono"}
														</p>
														<p className="text-slate-500 text-xs mb-1">
															<span className="font-semibold">
																Espacio de interes:
															</span>{" "}
															{req.tipo_espacio}
														</p>
														<p className="text-slate-600 text-sm leading-relaxed whitespace-pre-wrap">
															{req.mensaje || "Sin mensaje adicional."}
														</p>
														<div className="flex items-center gap-2 mt-3">
															<a
																href={`mailto:${req.email}`}
																className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
															>
																<i className="ri-reply-line" /> Responder
															</a>
															<button
																onClick={async (e) => {
																	e.stopPropagation();
																	await supabase
																		.from("advertising_requests")
																		.delete()
																		.eq("id", req.id);
																	setAdRequests((prev) =>
																		prev.filter((m) => m.id !== req.id),
																	);
																	setSelectedAdRequest(null);
																}}
																className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-bold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
															>
																<i className="ri-delete-bin-line" /> Eliminar
															</button>
														</div>
													</div>
												</div>
											)}
										</div>
									))}
								</div>
							)}
						</div>
					</div>
				</div>

				{/* ── ESTADÍSTICAS TAB ── */}
				<div className={activeTab === "estadisticas" ? "block" : "hidden"}>
					<div className="space-y-6">
						<div>
							<h2
								className="text-[#0B1F3A] font-extrabold text-2xl mb-1"
								style={{ fontFamily: "'Sora', sans-serif" }}
							>
								Noticias de Estadísticas
							</h2>
							<p className="text-slate-500 text-sm">
								Gestiona las noticias que aparecen en la sección de estadísticas
								del portal.
							</p>
						</div>
						<StatsNewsEditor />
					</div>
				</div>

				{/* ── CATEGORÍAS TAB ── */}
				<div className={activeTab === "categorias" ? "block" : "hidden"}>
					<div className="space-y-6">
						<div>
							<h2
								className="text-[#0B1F3A] font-extrabold text-2xl mb-1"
								style={{ fontFamily: "'Sora', sans-serif" }}
							>
								Gestión de Categorías
							</h2>
							<p className="text-slate-500 text-sm">
								Agrega, edita, elimina y reordena las categorías del portal.
							</p>
						</div>
						{loadingCategories ? (
							<div className="p-10 flex items-center justify-center">
								<i className="ri-loader-4-line animate-spin text-slate-300 text-3xl" />
							</div>
						) : (
							<CategoryManager
								categories={categories}
								onRefresh={refetchCategories}
							/>
						)}
					</div>
				</div>
			</div>
		</main>
	);
}
